import type { PropsWithChildren, ReactNode } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/Card/HoverCard';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { TextVariant } from '@/components/Typography/Text.variant';
import { cn } from '@/lib/cn';
type Props = PropsWithChildren<{
  /**
   * 表示するテキスト or Component
   */
  label?: string | ReactNode;
  /**
   * サブラベルのテキスト or Component
   * メインラベルの下に小さく表示されます
   */
  subLabel?: string | ReactNode;
  /**
   * サイズバリエーション
   * - "ExtraLarge": 最大サイズ（40px）
   * - "Large": 大きいサイズ（32px）
   * - "Default": デフォルトサイズ（24px）
   * - "Small": 小さいサイズ（16px）
   * - "ExtraSmall": 極小サイズ（12px）
   */
  size?: 'ExtraLarge' | 'Large' | 'Default' | 'Small' | 'ExtraSmall';
  /**
   * ラベルを表示するかどうか
   */
  showLabel?: boolean;
  /**
   * クリック時のイベントハンドラ
   */
  onClick?: () => void;
  /**
   * クラス名
   */
  className?: string;
  /**
   * プレビューカードの内容
   * 指定されている場合、ホバー時にプレビューカードが表示されます
   */
  previewCardContent?: ReactNode;
  /**
   * プレビューカードのクラス名
   */
  previewCardClassName?: string;
}>;

/**
 * アバターとラベルを表示するコンポーネントです。
 * 子要素に指定した内容をアバター部分に表示します。
 */
export function LabelXBase({
  label,
  subLabel,
  size = 'Default',
  showLabel = true,
  onClick,
  className,
  previewCardContent,
  previewCardClassName,
  children
}: Props) {
  // サイズに基づくテキストのバリアント設定
  const textVariant = {
    ExtraLarge: TextVariant.h2,
    Large: TextVariant.labelLarge,
    Default: TextVariant.labelSemiLarge,
    Small: TextVariant.label,
    ExtraSmall: TextVariant.labelSmall
  }[size];

  // サイズに基づくサブラベルのテキストバリアント設定（一段階小さいサイズ）
  const subTextVariant = {
    ExtraLarge: TextVariant.labelLarge,
    Large: TextVariant.labelSemiLarge,
    Default: TextVariant.label,
    Small: TextVariant.labelSmall,
    ExtraSmall: TextVariant.labelSmallReg
  }[size];

  // サイズに基づくギャップサイズ設定
  const gapSize = {
    ExtraLarge: 'gap-3',
    Large: 'gap-2',
    Default: 'gap-2',
    Small: 'gap-1',
    ExtraSmall: 'gap-1'
  }[size];
  const content = <HStack className={cn('items-center', gapSize, (onClick || previewCardContent) && 'cursor-pointer', className)} onClick={onClick}>
      {children}
      {showLabel && (label || subLabel) && <VStack className="gap-0">
          {label && (typeof label === 'string' ? <Text variant={textVariant} className="text-neutral-on-fill">
                {label}
              </Text> : label)}
          {subLabel && (typeof subLabel === 'string' ? <Text variant={subTextVariant} className="text-neutral-muted-on-fill">
                {subLabel}
              </Text> : subLabel)}
        </VStack>}
    </HStack>;

  // プレビューカードコンテンツがない場合は通常の表示
  if (!previewCardContent) {
    return content;
  }

  // プレビューカードコンテンツがある場合はホバーカードで表示
  // button要素を使用することで、DataTable の行クリックよりホバーを優先させる
  return <HoverCard openDelay={150} closeDelay={150}>
      <HoverCardTrigger asChild>
        <button type="button" className="text-left" tabIndex={-1}>
          {content}
        </button>
      </HoverCardTrigger>
      <HoverCardContent className={cn('p-4', previewCardClassName)}>
        {previewCardContent}
      </HoverCardContent>
    </HoverCard>;
}